// ============================================================================
// Visual HTML editor — contentEditable-based WYSIWYG with @-mention variables.
//
// Designed specifically for the email-template editor in Settings. The admin
// types in plain English, sees formatting live, and types @ to insert template
// variables (which render as coloured chips in-editor but serialize back to
// plain `{variable_name}` tokens in the stored HTML — matching the server's
// str.format_map substitution).
//
// Why roll our own instead of a TipTap/Slate/Quill dependency:
//   - zero new deps (keeps the bundle lean)
//   - we only need 6 toolbar actions + 1 mention behaviour — a full framework
//     is overkill for a single form field
//   - the output is simple sanitised HTML that Resend can render consistently
//
// Trade-offs accepted:
//   - we use document.execCommand (officially deprecated but still works in
//     every modern browser + all email-drafting tools still rely on it)
//   - no collaborative editing, no undo stack beyond the browser's built-in
// ============================================================================
import { useEffect, useRef, useState, useLayoutEffect } from "react";
import DOMPurify from "isomorphic-dompurify";
import { Bold, Italic, Underline, List, Link as LinkIcon, Heading, Code2, Eye } from "lucide-react";

const VAR_TOKEN_RE = /\{([a-z_][a-z0-9_]*)\}/g;

// Phase 1 — sanitize HTML before assigning to .innerHTML. Defense-in-depth
// even though the source is admin-authored: a future seed-imported template
// or a paste from an untrusted source shouldn't be able to execute scripts
// in the editor surface. Profile is "html" + we whitelist the chip
// attributes we render with via VisualHtmlEditor.
const SANITIZER_CONFIG = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ["target", "rel", "data-var", "contenteditable"],
};

// Convert stored HTML (which may contain raw {foo} tokens OR existing chip
// spans) into the chip-rich HTML the contentEditable displays. Idempotent.
function hydrateChips(html, variables) {
  if (!html) return "";
  const validKeys = new Set((variables || []).map((v) => v.key));
  // First strip any OLD chip spans so we don't double-wrap on reload.
  let out = html.replace(
    /<span[^>]*data-var="([a-z_][a-z0-9_]*)"[^>]*>\{?([^<]*?)\}?<\/span>/gi,
    "{$1}",
  );
  // Then wrap fresh {foo} tokens in chip spans.
  out = out.replace(VAR_TOKEN_RE, (match, key) => {
    if (!validKeys.has(key)) return match;  // unknown var → leave as plain text
    return `<span class="var-chip" data-var="${key}" contenteditable="false">{${key}}</span>`;
  });
  return out;
}

// Reverse of hydrate: strip chip styling back to plain `{foo}` for the server.
function dehydrateChips(html) {
  if (!html) return "";
  return html.replace(
    /<span[^>]*data-var="([a-z_][a-z0-9_]*)"[^>]*>[^<]*<\/span>/gi,
    "{$1}",
  );
}

export function VisualHtmlEditor({ value, onChange, variables = [], testid }) {
  const editorRef = useRef(null);
  const [isSource, setIsSource] = useState(false);
  const [sourceDraft, setSourceDraft] = useState(value || "");
  // @-mention popover state
  const [mention, setMention] = useState(null);  // {query, x, y, range}
  const [selectedMentionIdx, setSelectedMentionIdx] = useState(0);

  // Only push `value` → DOM when it diverges from what's currently in the
  // editor. Otherwise every keystroke (onInput fires setValue, parent
  // re-renders, we'd reset the DOM and lose caret). Track hydrated version.
  const lastHydratedRef = useRef(null);
  useLayoutEffect(() => {
    if (isSource) return;
    const el = editorRef.current;
    if (!el) return;
    const hydrated = hydrateChips(value || "", variables);
    const sanitized = DOMPurify.sanitize(hydrated, SANITIZER_CONFIG);
    if (lastHydratedRef.current !== sanitized && el.innerHTML !== sanitized) {
      el.innerHTML = sanitized;
      lastHydratedRef.current = sanitized;
    }
  }, [value, variables, isSource]);

  useEffect(() => { setSourceDraft(value || ""); }, [value]);

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    commit();
    editorRef.current?.focus();
  };

  const commit = () => {
    const el = editorRef.current;
    if (!el) return;
    const html = dehydrateChips(el.innerHTML);
    lastHydratedRef.current = el.innerHTML;
    onChange?.(html);
  };

  const insertVarAtCaret = (key) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    // Insert chip HTML at current caret using execCommand (preserves undo).
    const chip = `<span class="var-chip" data-var="${key}" contenteditable="false">{${key}}</span>&nbsp;`;
    document.execCommand("insertHTML", false, chip);
    commit();
  };

  // --- @-mention handling -------------------------------------------------

  const closeMention = () => { setMention(null); setSelectedMentionIdx(0); };

  const filteredVars = mention
    ? variables.filter((v) => {
        const q = mention.query.toLowerCase();
        return !q || v.key.toLowerCase().includes(q) || v.label.toLowerCase().includes(q);
      })
    : [];

  const onKeyDown = (e) => {
    if (mention) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedMentionIdx((i) => Math.min(i + 1, filteredVars.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setSelectedMentionIdx((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const pick = filteredVars[selectedMentionIdx];
        if (pick) pickMentionVar(pick.key);
        return;
      }
      if (e.key === "Escape") { e.preventDefault(); closeMention(); return; }
    }
  };

  const onInput = () => {
    commit();
    // Detect @-trigger: look at the text node immediately before the caret.
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) { closeMention(); return; }
    const text = node.textContent.slice(0, range.startOffset);
    const m = /@([a-zA-Z_]*)$/.exec(text);
    if (!m) { closeMention(); return; }
    // Position popover just below the caret.
    const rect = range.getBoundingClientRect();
    const editorRect = editorRef.current.getBoundingClientRect();
    setMention({
      query: m[1],
      x: rect.left - editorRect.left,
      y: rect.bottom - editorRect.top + 4,
      // Save absolute text-node info so we can delete the @-query when committing.
      node,
      startOffset: range.startOffset - m[0].length,
      endOffset: range.startOffset,
    });
    setSelectedMentionIdx(0);
  };

  const pickMentionVar = (key) => {
    const m = mention;
    closeMention();
    if (!m) return;
    // Delete the @query text, then insert chip at that position.
    try {
      const range = document.createRange();
      range.setStart(m.node, m.startOffset);
      range.setEnd(m.node, m.endOffset);
      range.deleteContents();
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      insertVarAtCaret(key);
    } catch { /* node may have been replaced — fall back to insert at caret */ }
  };

  const addLink = () => {
    const url = window.prompt("Enter URL (https://…)");
    if (url) exec("createLink", url);
  };

  return (
    <div className="border border-[var(--line-2)] rounded-sm bg-[var(--surface)]" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-[var(--line)] bg-[var(--surface-2)]">
        <ToolbarBtn onClick={() => exec("bold")}          title="Bold (Ctrl+B)"><Bold size={13} /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("italic")}        title="Italic (Ctrl+I)"><Italic size={13} /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("underline")}     title="Underline (Ctrl+U)"><Underline size={13} /></ToolbarBtn>
        <div className="w-px h-4 bg-[var(--line)] mx-1" />
        <ToolbarBtn onClick={() => exec("formatBlock", "<h3>")} title="Heading"><Heading size={13} /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("insertUnorderedList")}  title="Bulleted list"><List size={13} /></ToolbarBtn>
        <ToolbarBtn onClick={addLink}                     title="Add link"><LinkIcon size={13} /></ToolbarBtn>
        <div className="flex-1" />
        <span className="text-[10.5px] text-[var(--muted)] mr-2 hidden sm:block">Type <kbd className="px-1 py-0.5 bg-white border border-[var(--line-2)] rounded-sm font-mono text-[10px]">@</kbd> for variables</span>
        <ToolbarBtn onClick={() => setIsSource((s) => !s)} title={isSource ? "Visual editor" : "View HTML source"} active={isSource}>
          {isSource ? <Eye size={13} /> : <Code2 size={13} />}
        </ToolbarBtn>
      </div>

      {/* Editor surface */}
      {isSource ? (
        <textarea
          value={sourceDraft}
          onChange={(e) => { setSourceDraft(e.target.value); onChange?.(e.target.value); }}
          rows={10}
          className="w-full px-3 py-2 text-[12.5px] font-mono bg-[var(--surface)] border-0 rounded-b-sm focus:outline-none resize-y"
          data-testid="tpl-html-source"
        />
      ) : (
        <div className="relative">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={onInput}
            onKeyDown={onKeyDown}
            onBlur={commit}
            data-testid="tpl-html-visual"
            className="px-4 py-3 min-h-[220px] text-[13px] leading-relaxed text-[var(--ink-2)] focus:outline-none email-editor-surface"
          />
          {mention && (
            <div
              className="absolute z-10 bg-[var(--surface)] border border-[var(--line-2)] rounded-sm shadow-lg py-1 min-w-[220px] max-h-60 overflow-auto"
              style={{ left: mention.x, top: mention.y }}
              data-testid="mention-popover"
            >
              {filteredVars.length === 0 && <div className="px-3 py-1.5 text-[11.5px] text-[var(--muted)]">No match for "@{mention.query}"</div>}
              {filteredVars.map((v, i) => (
                <button
                  key={v.key}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pickMentionVar(v.key); }}
                  onMouseEnter={() => setSelectedMentionIdx(i)}
                  data-testid={`mention-${v.key}`}
                  className={`w-full text-left px-3 py-1.5 text-[12.5px] flex items-center justify-between gap-3 ${i === selectedMentionIdx ? "bg-[var(--accent)] text-white" : "hover:bg-[var(--surface-2)]"}`}
                >
                  <span className="font-medium">{v.label}</span>
                  <span className={`text-[10.5px] font-mono ${i === selectedMentionIdx ? "opacity-80" : "text-[var(--muted)]"}`}>{`{${v.key}}`}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Chip styles — scoped to this editor + email preview via class name */}
      <style>{`
        .email-editor-surface .var-chip,
        .email-editor-surface [data-var] {
          display: inline-block;
          padding: 1px 6px;
          margin: 0 1px;
          background: var(--accent, #2563EB);
          color: #fff;
          border-radius: 2px;
          font-family: ui-monospace, monospace;
          font-size: 11.5px;
          font-weight: 600;
          white-space: nowrap;
          vertical-align: baseline;
          user-select: all;
        }
        .email-editor-surface:empty::before {
          content: "Start typing your email. Use the toolbar above to format, or type @ to add a variable like @partner_name.";
          color: var(--muted, #9CA3AF);
          font-style: italic;
          pointer-events: none;
        }
        .email-editor-surface p { margin: 0 0 8px 0; }
        .email-editor-surface h3 { font-size: 16px; font-weight: 600; margin: 12px 0 6px 0; color: var(--ink); }
        .email-editor-surface ul { padding-left: 20px; margin: 0 0 8px 0; list-style: disc; }
        .email-editor-surface a { color: var(--accent, #2563EB); text-decoration: underline; }
      `}</style>
    </div>
  );
}

function ToolbarBtn({ children, onClick, title, active = false }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`h-7 w-7 inline-flex items-center justify-center rounded-sm transition-colors ${active ? "bg-[var(--accent)] text-white" : "text-[var(--ink-2)] hover:bg-white hover:text-[var(--accent)]"}`}
    >{children}</button>
  );
}

export default VisualHtmlEditor;
